import { DateService } from '@hospitality-bot/shared/utils';
import { DiscountType } from '../constants';
import { PageRoutes } from '@hospitality-bot/admin/shared';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { toCurrency } from './valueFormatter';

type DiscountCalcValues = {
  price: number;
  discountedPrice: number;
  discountValue: number;
};

export const discountCalculation = (
  data: Partial<DiscountCalcValues>,
  discountType?: DiscountType
): (DiscountCalcValues & { isPatch: boolean }) | null => {
  if (!discountType) return null;

  const { price, discountValue, discountedPrice } = data;
  const isNum = discountType === DiscountType.FLAT;

  if (price && discountValue) {
    return {
      price,
      discountedPrice: isNum
        ? price - discountValue
        : price - (price * discountValue) / 100,
      discountValue,
      isPatch: !(isNum && discountValue > price),
    };
  }

  if (price && discountedPrice) {
    return {
      price,
      discountedPrice,
      discountValue: isNum
        ? price - discountedPrice
        : 100 * ((price - discountedPrice) / price),
      isPatch: true,
    };
  }

  return null;
};

/**
 * Manage and Reset a Delayed Execution Timer
 *
 * This utility function schedules a callback to execute after a specified delay.
 * It ensures that the timeout is automatically cleared after execution, preventing unnecessary lingering timers.
 *
 * @param callback - A function that will be executed after the specified delay.
 * @param delay - (Optional) The time in milliseconds before executing the callback. Defaults to `0`.
 *
 */
export function timeoutGuard(callback: () => void, delay: number = 0) {
  // Set a new timeout and return its ID
  const newTimeoutId = setTimeout(() => {
    callback();
    clearTimeout(newTimeoutId); // Ensure timeout is cleared after execution
  }, delay);
  return newTimeoutId;
}

/**
 * Returns start and end time of a given date
 * @param fromDate - The input date
 * @param toDate - The input date
 * @returns Object with startOfDay and endOfDay
 */
export function getFirstLastDate(fromDates: Date, toDates: Date) {
  const fromDate = new Date(fromDates);
  fromDate.setHours(0, 0, 0, 0);

  const toDate = new Date(toDates);
  toDate.setHours(23, 59, 59, 999);

  return { fromDate: fromDate.getTime(), toDate: toDate.getTime() };
}

/**
 *
 * @param control
 * here we pass the control that we need to add the validators
 * @param validatiors
 * Validators is the array of the validatiors and if we doesn't pass any validator it will clear all the validators
 * @param isAllowValidators
 * You can add or remove condition Accordingly
 */

export function updateFormControlValidators(
  control: AbstractControl | null,
  validatiors?: ValidatorFn[],
  isAllowValidators = true,
  emitEvent = true
) {
  if (control) {
    if (validatiors && isAllowValidators) {
      control.setValidators(validatiors);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent });
  }
}

export function getRandomLightHexColor() {
  // Generate random values for RGB components
  var r = Math.floor(Math.random() * 128) + 128; // Red component biased towards higher values
  var g = Math.floor(Math.random() * 128) + 128; // Green component biased towards higher values
  var b = Math.floor(Math.random() * 128) + 128; // Blue component biased towards higher values

  // Convert RGB components to hex and concatenate
  var hexColor =
    '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);

  return hexColor;
}

export function generateArrayItemColor(
  index: number,
  count: number,
  type: 'dark' | 'light' = 'light'
) {
  const saturation = type === 'light' ? 50 : 60; // Saturation value (0-100)

  // Determine lightness based on the type parameter
  const lightness = type === 'light' ? 80 : 30; // Adjust as needed

  const hue = (360 / count) * index; // Vary the hue based on the index
  const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

  return color;
}

export function getListOfRandomLightColor(numberOfColors: number) {
  return Array.from({ length: numberOfColors }, getRandomLightHexColor);
}

export type DaysType =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export const weeks: {
  label: string;
  value: DaysType;
}[] = [
  { label: 'Sun', value: 'SUNDAY' },
  { label: 'Mon', value: 'MONDAY' },
  { label: 'Tue', value: 'TUESDAY' },
  { label: 'Wed', value: 'WEDNESDAY' },
  { label: 'Thu', value: 'THURSDAY' },
  { label: 'Fri', value: 'FRIDAY' },
  { label: 'Sat', value: 'SATURDAY' },
];

export function epochWithoutTime(epoch: number) {
  // Create a new Date object using the epoch timestamp
  const date = new Date(epoch); // multiply by 1000 for milliseconds

  // Extract the date components
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // months are zero-indexed, so add 1
  const day = date.getDate();

  // Construct the date string in the format YYYY-MM-DD
  const formattedDate =
    year +
    '-' +
    (month < 10 ? '0' : '') +
    month +
    '-' +
    (day < 10 ? '0' : '') +
    day;

  const newDate = new Date(formattedDate);

  return newDate.getTime();
}

export function getDayOfWeekFromEpoch(epoch: number) {
  const date = new Date(epoch);
  const dayIndex = date.getUTCDay();

  return { day: weeks[dayIndex].value, dayIndex };
}

/**
 *
 * @returns unique random id
 */
export function generateUniqueId() {
  let timestamp = new Date().getTime();
  const uniqueId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      const r = (timestamp + Math.random() * 16) % 16 | 0;
      timestamp = Math.floor(timestamp / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uniqueId;
}

/**
 * @function getUniqueOptions
 * @param data original data
 * @returns uninque option
 */
export function getUniqueOptions<T extends Record<string, any>>(
  data: T[],
  uniqueKey: keyof T = 'value'
): T[] {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const uniqueMap = new Map<T[keyof T], T>();
  for (const item of data) {
    if (item && uniqueKey in item) {
      uniqueMap.set(item[uniqueKey], item);
    }
  }

  return Array.from(uniqueMap.values());
}

export function deepCopy<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepCopy) as any;
  }

  const copy: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      copy[key] = deepCopy(obj[key]);
    }
  }

  return copy as T;
}

export function getCalendarType(startDate, endDate, timezone) {
  const dateDiff = DateService.getDateDifference(startDate, endDate, timezone);
  const monthDiff = DateService.getMonthDifference(
    startDate,
    endDate,
    timezone
  );
  if (dateDiff >= 0 && dateDiff < 30) {
    return 'date';
  } else if (dateDiff >= 30 && dateDiff <= 98) {
    return 'week';
  } else if (dateDiff > 98 && monthDiff <= 14) {
    return 'month';
  } else {
    return 'year';
  }
}

export function getFullName(firstName: string, lastName: string) {
  return `${firstName ? firstName.trim() : ''}${
    firstName && lastName ? ' ' : ''
  }${lastName ? lastName.trim() : ''}`;
}

export function getProfileNickName(name: string) {
  return name
    ?.split(' ')
    ?.map((substr: string, index: number) => {
      if ([0, 1]?.includes(index)) return substr?.charAt(0);
      else return '';
    })
    ?.join('')
    ?.toUpperCase();
}

export function getDelimiter(value: string): number {
  const delimiters: Record<string, number> = {
    half: 2,
    full: 1,
    'one-fourth': 4,
    'one-third': 3,
  };

  return delimiters[value] ?? 1;
}

/**
 * Downloads a file from the given url and saves it to the user's computer.
 * @param {string} url The URL of the file to download.
 * @param {string} fileName The desired name of the downloaded file.
 * @returns {Promise<void>}
 */
export async function downloadFile(url: string, fileName: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
  }
}

export function getPageRoutes<
  T extends Record<string, PageRoutes>,
  K extends keyof T
>(routes: T, key: K, config?: keyof PageRoutes) {
  return routes[key];
}

export function paymentDescriptionGenerator(
  paymentMethod: string,
  amount: number,
  transactionId?: string,
  remarks?: string
) {
  const amountText = toCurrency(amount);

  // Base description
  let description = `Payment Received: ${paymentMethod}`;

  // Handle remarks
  if (remarks?.trim()) {
    description += ` (${remarks.trim()})`;
  }

  // Handle transaction ID
  if (transactionId?.trim()) {
    description = `[${transactionId.trim()}] ${description}`;
  }

  return description;
}
